import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import type { UsersQuery } from './use-users';

// Filtros de la pantalla de Usuarios sincronizados con la URL (deep-linking,
// UX-NAV-02). El cursor de paginación NO va a la URL: es estado efímero de scroll.

export type StatusSegment = 'todos' | 'activos' | 'pendientes' | 'desactivados';

export const STATUS_SEGMENTS: { value: StatusSegment; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'activos', label: 'Activos' },
  { value: 'pendientes', label: 'Pendientes' },
  { value: 'desactivados', label: 'Desactivados' },
];

const SEGMENT_STATUS: Record<Exclude<StatusSegment, 'todos'>, string> = {
  activos: 'active',
  pendientes: 'pending',
  desactivados: 'inactive',
};

export interface UsersFilters {
  q: string;
  segment: StatusSegment;
}

export function parseStatusSegment(raw: string | null): StatusSegment {
  return STATUS_SEGMENTS.some((s) => s.value === raw) ? (raw as StatusSegment) : 'todos';
}

/** Filtros de la vista → parámetros del endpoint. Puro; omite lo vacío. */
export function toUsersQuery(filters: UsersFilters): UsersQuery {
  const q = filters.q.trim();
  return {
    ...(q ? { q } : {}),
    ...(filters.segment !== 'todos' ? { status: SEGMENT_STATUS[filters.segment] } : {}),
  };
}

export interface UseUsersFilters {
  filters: UsersFilters;
  setQuery: (q: string) => void;
  setSegment: (s: StatusSegment) => void;
  /** Quita búsqueda y estado en UNA actualización: dos setSearchParams seguidos se
   *  pisan (el segundo parte de la URL previa y repone lo que el primero borró). */
  clear: () => void;
}

export function useUsersFilters(): UseUsersFilters {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<UsersFilters>(
    () => ({ q: params.get('q') ?? '', segment: parseStatusSegment(params.get('estado')) }),
    [params],
  );

  const patch = useCallback(
    (key: string, value: string | null) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (value === null || value === '') {
            p.delete(key);
          } else {
            p.set(key, value);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setQuery = useCallback((q: string) => patch('q', q.trim() ? q : null), [patch]);
  const setSegment = useCallback(
    (s: StatusSegment) => patch('estado', s === 'todos' ? null : s),
    [patch],
  );

  const clear = useCallback(() => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('q');
        p.delete('estado');
        return p;
      },
      { replace: true },
    );
  }, [setParams]);

  return { filters, setQuery, setSegment, clear };
}
